/**
 * Reads the amounts on a receipt from its OCR text, so a model does not have
 * to copy them.
 *
 * Copying a number is where a model slips most: Apple's on-device model read
 * `PEPPAR RÖD KL 1 4,35` as 14,35, `FRESH SLANGKLÄMMA 2 89,00` as 289,
 * `1 299,00` as 299, and took "Återstår att betala 0.00" for the total; a
 * cloud model drops a digit or swaps two now and then. OCR, for all its
 * faults, reads digits well - nearly every amount on a receipt is in its text
 * verbatim. So the model is asked which lines are products, and the amounts
 * are read off those lines here: the `ground-items` and `total-vote` steps,
 * and the phone's on-device reader.
 */

/** Amounts are handled in öre, so sums compare exactly. */
export type Ore = number;

export interface ReceiptLine {
  /** Position among the non-empty lines, from 0. */
  index: number;
  text: string;
  /** Amounts printed on the line, left to right. */
  amounts: Ore[];
  /**
   * Readings with a space as thousands separator: `1 299,00` is 1 299 kronor,
   * but `Lunch 215 215.00` is not 215 215. Which one a line means is decided
   * by the sum, so these are kept apart from `amounts`.
   */
  spaced: Ore[];
}

/**
 * `12,95`, `10.00`, `3.027,25`, `1,899.00`, `-3,00`, `500.00-A`. Not a rate
 * (`12.00%`), not part of a date (`24.07.13`), not three decimals (`0,562kg`).
 */
const AMOUNT = /(?<![\d.,])(-\s?)?(\d{1,3}(?:\.\d{3})+|\d{1,3}(?:,\d{3})+(?=\.\d{2})|\d+)[,.](\d{2})(?![\d%])(?![.,]\d)(?!\s%)(-(?!\d))?/g;
const SPACED_AMOUNT = /(?<![\d.,])(-\s?)?(\d{1,3}(?: \d{3})+)[,.](\d{2})(?![\d%])(?![.,]\d)(-(?!\d))?/g;

function toOre(integer: string, decimals: string, negative: boolean): Ore {
  const ore = Number(integer.replace(/[ .,]/g, '')) * 100 + Number(decimals);
  return negative ? -ore : ore;
}

function amountsIn(text: string, pattern: RegExp): Ore[] {
  const found: Ore[] = [];
  pattern.lastIndex = 0;
  for (let match = pattern.exec(text); match; match = pattern.exec(text)) {
    found.push(toOre(match[2]!, match[3]!, Boolean(match[1] || match[4])));
  }
  return found;
}

/** The amount a string holds, in öre, or `null`. */
export function parseOre(value: unknown): Ore | null {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.round(value * 100);
  if (typeof value !== 'string') return null;
  const amounts = amountsIn(value.trim(), AMOUNT);
  if (amounts.length > 0) return amounts[amounts.length - 1]!;
  // The model's own readings come without öre too: `145`, `145.`, `-20`.
  const whole = /^(-)?\s?(\d{1,6})[.,]?\s?(-)?\s*(kr|sek)?$/i.exec(value.trim());
  return whole ? toOre(whole[2]!, '00', Boolean(whole[1] || whole[3])) : null;
}

export function formatOre(ore: Ore): string {
  const sign = ore < 0 ? '-' : '';
  const abs = Math.abs(ore);
  return `${sign}${Math.floor(abs / 100)},${String(abs % 100).padStart(2, '0')}`;
}

export function receiptLines(text: string): ReceiptLine[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line, index) => ({
      index,
      text: line,
      amounts: amountsIn(line, AMOUNT),
      spaced: amountsIn(line, SPACED_AMOUNT).filter((ore) => Math.abs(ore) >= 100_000),
    }));
}

/** Lines that state what was paid. */
const TOTAL_LABEL = /\b(att betala|totalt?|tot|summa|notabelopp|belopp|brutto)\b/i;
/** Lines that state how it was paid - usually the same amount again. */
const PAYMENT_LABEL =
  /(\b(kort|card|visa|mastercard|swish|kontant|kontanter|betalning|betalt|mottaget|nets|köp|kop)\b|bankkort|kontokort|kreditkort|kortbetalning|^\W*sek\s+-?\d)/i;

/** A total printed in whole kronor, with no öre: `ATT BETALA 314 SEK`, `Totalt 89:-`. */
const WHOLE_TOTAL = /(?<![\d.,])(-\s?)?(\d{1,6})\s*(?::-|kr\b|sek\b)\s*(-)?\s*$/i;
/** Totals that are not the amount paid. */
const NOT_PAID =
  /(delsumma|netto|\bnett\b|exkl|varuvärde|varuvarde|poäng|poang|bonus|underlag|tillbaka|växel|vaxel|återstår|aterstar|rabatt|avrundning|besparing|sparat)/i;

export interface PaidTotal {
  ore: Ore;
  /** The first line that states it: where the purchased lines end. */
  lineIndex: number;
}

/**
 * The amount paid, by vote.
 *
 * A receipt prints it several times - the total, the card or cash line, the
 * terminal's `KÖP SEK`, the VAT table's gross column - and prints the other
 * totals (net, VAT, change) once each. So every amount on a total or payment
 * line is a vote, a total line's counting double, and the amount with most
 * votes is the one paid. A line's largest amount stands for it: on
 * `TOTAL (Inkl. moms SEK 2.497,20) 12.486,00` and on a VAT row
 * `Total 59.90 11.98 47.92` that is the gross.
 *
 * Votes are by size, and the sign is the one most votes carry, so a return
 * (`TOTAL 89,00-`, `Kort online 89,00-`) is -89 rather than nothing. A total
 * rounded to whole kronor gives way to an amount within 50 öre of it that a
 * payment line states: a card is charged `259,70` however the till rounds,
 * while a cash payment votes for the rounded figure itself. A total of zero
 * - a bonus cheque that paid for everything - counts only when no line states
 * anything else.
 */
interface TotalVote {
  weight: number;
  labelled: number;
  /** Payment lines among the votes: what a card was actually charged. */
  paid: number;
  /** The share of `weight` that came with a minus sign. */
  negative: number;
  /** How many lines gave it a minus sign. */
  negativeLines: number;
  lineIndex: number;
}

export function findPaidTotal(lines: readonly ReceiptLine[]): PaidTotal | null {
  const votes = new Map<Ore, TotalVote>();
  let zero: PaidTotal | null = null;
  for (const line of lines) {
    if (NOT_PAID.test(line.text)) continue;
    if (/moms/i.test(line.text) && !/inkl/i.test(line.text)) continue;
    const labelled = TOTAL_LABEL.test(line.text);
    const payment = !labelled && PAYMENT_LABEL.test(line.text);
    if (!labelled && !payment) continue;
    const weight = labelled ? 2 : 1;
    const printed = [...line.amounts, ...line.spaced];
    if (labelled && printed.length === 0) {
      const whole = WHOLE_TOTAL.exec(line.text);
      if (whole) printed.push(toOre(whole[2]!, '00', Boolean(whole[1] || whole[3])));
    }
    const candidates = printed.filter((ore) => ore !== 0);
    if (candidates.length === 0) {
      if (labelled && printed.length > 0) zero ??= { ore: 0, lineIndex: line.index };
      continue;
    }
    const ore = candidates.reduce((largest, next) => (Math.abs(next) > Math.abs(largest) ? next : largest));
    const size = Math.abs(ore);
    const vote = votes.get(size) ?? { weight: 0, labelled: 0, paid: 0, negative: 0, negativeLines: 0, lineIndex: line.index };
    vote.weight += weight;
    if (labelled) vote.labelled += 1;
    if (payment) vote.paid += 1;
    if (ore < 0) {
      vote.negative += weight;
      vote.negativeLines += 1;
    }
    votes.set(size, vote);
  }

  // A return says so on the total and the refund alike. A minus on one line
  // alone is more likely a rule or a smudge OCR read as one
  // (`**TOTALT (INKL MOMS) -1819.00`), and no total is better than a refund
  // nobody made: a missing total is flagged for a look.
  for (const [size, vote] of votes) {
    if (vote.negative * 2 > vote.weight && vote.negativeLines < 2) votes.delete(size);
  }

  let best: [Ore, TotalVote] | null = null;
  for (const [size, vote] of votes) {
    if (!best || vote.weight > best[1].weight || (vote.weight === best[1].weight && vote.labelled > best[1].labelled)) {
      best = [size, vote];
    }
  }
  if (!best) return zero;

  if (best[0] % 100 === 0) {
    let precise: [Ore, TotalVote] | null = null;
    for (const [size, vote] of votes) {
      if (size % 100 === 0 || Math.abs(size - best[0]) >= 50 || vote.paid === 0) continue;
      if (!precise || vote.weight > precise[1].weight) precise = [size, vote];
    }
    if (precise) best = precise;
  }

  const [size, vote] = best;
  // The purchases end at the first total line, rounded or not.
  let lineIndex = vote.lineIndex;
  for (const [other, entry] of votes) {
    if (entry.labelled > 0 && Math.abs(other - size) < 50) lineIndex = Math.min(lineIndex, entry.lineIndex);
  }
  return { ore: vote.negative * 2 > vote.weight ? -size : size, lineIndex };
}

/**
 * The lines up to and including the total: where the purchases are. What
 * follows - the card terminal's slip, VAT tables, opening hours, return
 * policy - holds nothing the product list needs, and on a small model every
 * line of it is one more place to find a "product".
 */
export function purchaseSection(lines: readonly ReceiptLine[], total: PaidTotal | null): ReceiptLine[] {
  if (!total) return [...lines];
  return lines.slice(0, total.lineIndex + 1);
}

/** The section as the model sees it: one numbered line each. */
export function numberedText(lines: readonly ReceiptLine[]): string {
  return lines.map((line, position) => `${position + 1}: ${line.text}`).join('\n');
}

/** A product line as the model gave it: where it is printed, and what it read there. */
export interface ModelItem {
  /** 1-based, in the numbered text the model was shown. */
  line?: number | null;
  name: string;
  price?: string | number | null;
}

export interface GroundedItem {
  name: string;
  totalPrice: string;
  /** The line the amount was read from, if one was found. */
  lineIndex: number | null;
  /** The line the product is named on, if it was placed. */
  nameLine?: number | null;
}

/**
 * Lines that are never a purchase. Names are compared from their start, as
 * OCR prints them and as the model copies them.
 */
const NOT_A_PRODUCT =
  /^\W*(att betala|totalt?|tot\b|summa|delsumma|netto|moms|brutto|öresavrundning|avrundning|kontokort|bankkort|kreditkort|kort\b|card\b|visa\b|mastercard|swish|kontant|växel|tillbaka|betalt|betalning|mottaget|erhållet|kvitto|kassör|kassa\b|weight|vikt\b|datum|tid\b|org\.?\s*(nr|no|:)|term\b|ref\b|aid\b|tvr\b|mid\b|ptid|butik|säljare|betjänad|kundnr|kundenr|medlem|antal\b|köp\b|godkänt|spara kvittot|slut självscanning|start självscanning|subtotal|du sparar|rabatter\b|summering|ej poänggrundande|kontaktlös|contactless|self service|sekt\s?ion\s*:)/i;

/** What a product line carries under it: size, colour, serial numbers. */
const DETAIL = /^\W*(size|storlek|stl\b|color|colour|färg\s*:|garanti|imei|art\.?\s?nr|artikelnr|ean\b|serienr|serial|s\/n)/i;

function fold(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

function similar(name: string, line: string): boolean {
  const a = fold(name);
  const b = fold(line);
  if (a.length < 2 || b.length < 2) return false;
  if (b.includes(a)) return true;
  // Too few letter pairs for a share of them to mean anything: `Bröd` is not `PEPPAR RÖD`.
  if (a.length < 6) return false;
  const grams = (value: string) => Array.from({ length: value.length - 1 }, (_, i) => value.slice(i, i + 2));
  const pool = grams(b);
  let common = 0;
  for (const gram of grams(a)) {
    const at = pool.indexOf(gram);
    if (at >= 0) {
      common += 1;
      pool.splice(at, 1);
    }
  }
  return common / Math.max(1, a.length - 1) >= 0.6;
}

/**
 * A row of numbers only: `1 * 199.00 199.00`, `A 39,00 117,00`,
 * `0,562kg*219,00kr/kg 123,08`, `902299 25% 2495.00`. It prices the product
 * named next to it rather than being one.
 */
function isPriceRow(line: ReceiptLine): boolean {
  if (line.amounts.length === 0) return false;
  const words = line.text.match(/\p{L}{3,}/gu) ?? [];
  return words.every((word) => PRICE_ROW_WORDS.has(word.toLowerCase()));
}

/**
 * What is left of a quantity or unit-price row once its numbers are cleaned
 * away: `2 ×`, `a`, `/kg`, `k/st`. A product's name has more to it, a word or
 * at least an article number (`392400`).
 */
function isFragment(name: string): boolean {
  return !/\p{L}{2}|\d{4}/u.test(name) || /^\W*\p{L}?\s*\/\s*\p{L}{1,3}\W*$/u.test(name);
}

/** Units and currency, and `Ist`, OCR's usual reading of `1st`. */
const PRICE_ROW_WORDS = new Set(['sek', 'pcs', 'ist', 'stk', 'kgs']);

/** A table's column headings: `Artikel Belopp`, `St Namn Pris Total`. */
function isColumnHeader(line: ReceiptLine): boolean {
  return /\b(artikel|belopp|varubeskrivning|benämning|namn|pris|ant\.?)\b/i.test(line.text) && line.amounts.length === 0;
}

/** Where looking for an item's amount must stop: the totals and payment lines. */
function isStop(line: ReceiptLine): boolean {
  return NOT_A_PRODUCT.test(line.text) || (TOTAL_LABEL.test(line.text) && !isPriceRow(line));
}

/**
 * The row an item's amount is on: its own line; or, for a name printed on a
 * line of its own (`FLANKSTEK` over `0,562kg*219,00kr/kg 123,08`, a Biltema
 * article over `1 * 199.00 199.00`), the price row just below it, past detail
 * lines but not past another product or a total; or, failing that, a price
 * row just above (Elgiganten prints `902299 25% 2495.00` over the name).
 */
function priceRow(lines: readonly ReceiptLine[], index: number, claimed: ReadonlySet<number>): ReceiptLine | null {
  if (lines[index]!.amounts.length > 0) return lines[index]!;
  for (let next = index + 1; next < Math.min(lines.length, index + 4); next += 1) {
    const line = lines[next]!;
    if (claimed.has(next) || isStop(line)) break;
    if (isPriceRow(line)) return line;
    if (line.amounts.length > 0) break;
  }
  const above = lines[index - 1];
  return above && !claimed.has(above.index) && isPriceRow(above) ? above : null;
}

/** A product name without the amounts, quantities and codes printed around it. */
export function cleanItemName(text: string): string {
  return text
    .replace(SPACED_AMOUNT, ' ')
    .replace(AMOUNT, ' ')
    .replace(/\d+(?:[,.]\d+)?\s*(st|kg|hg|g|l)?\s*[x*à@]\s*(\d+(?:[,.]\d+)?)?\s*(kr)?(\s*\/\s*(kg|st|l))?/gi, ' ')
    .replace(/\b(kr|sek)\b\.?/gi, ' ')
    .replace(/^\W*\d{5,}\s+(?=\p{L})/u, '')
    .replace(/^\W*\d{1,2}\s+(?=\p{L})/u, '')
    .replace(/^[\s+*=|.:-]+|[\s*=|:-]+$/g, '')
    .replace(/\s+[A-Za-z]$/, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/**
 * The model's items, their amounts read off the receipt.
 *
 * Each item is placed on its line - the number the model gave, checked against
 * the name, or else the line its name is printed on - and takes the last
 * amount of its price row: a row prints quantity, unit price and line total in
 * that order, and the model, left to it, picks the unit price or merges a
 * digit of the name into the amount.
 *
 * A quantity row the model listed as a product of its own is folded into the
 * name above it. Anything named like a total, payment or slip detail is
 * dropped, and so is an item with no line whose amount is printed nowhere.
 */
export function groundItems(
  items: readonly ModelItem[],
  section: readonly ReceiptLine[],
  allLines: readonly ReceiptLine[] = section,
): { items: GroundedItem[]; changed: number; dropped: string[] } {
  const printed = new Set(allLines.flatMap((line) => [...line.amounts, ...line.spaced]));
  const placed: (number | null)[] = [];
  const used = new Set<number>();

  for (const item of items) {
    let index: number | null = null;
    const given = typeof item.line === 'number' ? item.line - 1 : -1;
    // The model's line, or a neighbour: a name wrapped over two lines is given by either.
    for (const candidate of [given, given + 1, given - 1]) {
      if (candidate >= 0 && candidate < section.length && !used.has(candidate) && similar(item.name, section[candidate]!.text)) {
        index = candidate;
        break;
      }
    }
    if (index === null) {
      const found = section.find((line) => !used.has(line.index) && similar(item.name, line.text));
      index = found ? found.index : null;
    }
    // Named after the size or colour line under its row, but numbered right: the row it gave.
    if (index !== null && DETAIL.test(section[index]!.text) && given >= 0 && given < section.length && !used.has(given)) {
      const row = section[given]!;
      if (row.amounts.length > 0 && !isStop(row) && !DETAIL.test(row.text)) index = given;
    }
    if (index !== null) used.add(index);
    placed.push(index);
  }

  const claimed = new Set(placed.filter((index): index is number => index !== null));
  const grounded: GroundedItem[] = [];
  let changed = 0;
  const dropped: string[] = [];
  let current = '';
  let currentLine: number | null = null;
  const drop = () => {
    dropped.push(current);
  };

  /*
   * A price row the model gave as a product of its own belongs to the name
   * line above it. When that line is listed too - `FLANKSTEK`, then
   * `0,562kg*219,00kr/kg 123,08` - the row is released to price it; when it
   * is not, the row takes the name above as its own. Under a product priced
   * on its own line - `LÖK GUL 1,85` over `0,124 kg × 14,90 SEK/kg` - the
   * row is that product's weight and is no product either.
   */
  const rowOf = new Map<number, number>();
  const folded = new Set<number>();
  placed.forEach((index, position) => {
    if (index === null || !isPriceRow(section[index]!)) return;
    const priced = section[index - 1];
    if (priced && claimed.has(priced.index) && priced.amounts.length > 0 && !isPriceRow(priced) && !isStop(priced)) {
      // Still claimed, so a name below does not take its amount.
      folded.add(position);
      return;
    }
    const above = [index - 1, index - 2].find((at) => at >= 0 && section[at]!.amounts.length === 0 && !isStop(section[at]!) && !isColumnHeader(section[at]!));
    if (above === undefined) return;
    claimed.delete(index);
    if (claimed.has(above)) {
      folded.add(position);
      return;
    }
    claimed.add(above);
    placed[position] = above;
    rowOf.set(position, index);
  });

  items.forEach((item, position) => {
    current = item.name.trim();
    currentLine = null;
    if (folded.has(position)) return drop();
    const index = placed[position] ?? null;
    const read = parseOre(item.price);
    const own = rowOf.get(position);
    const renamed = index !== null && DETAIL.test(item.name) && !DETAIL.test(section[index]!.text);
    let name = (own !== undefined || renamed) && index !== null ? cleanItemName(section[index]!.text) : cleanItemName(item.name);
    if (!name && index !== null) name = cleanItemName(section[index]!.text);
    if (!name || isFragment(name) || NOT_A_PRODUCT.test(name) || DETAIL.test(name) || ADDRESS_NAME.test(name)) return drop();
    if (index !== null && (NOT_A_PRODUCT.test(section[index]!.text) || DETAIL.test(section[index]!.text))) return drop();
    currentLine = index;
    if (own !== undefined) return push(name, section[own]!, read);

    if (index !== null) {
      const row = priceRow(section, index, claimed);
      if (row) return push(name, row, read);
      // No amount to read, as in `CAPRICCIOSA 145.` over `00`: the line must at least print the kronor.
      const kronor = read === null || read <= 0 ? null : String(Math.trunc(read / 100));
      if (!kronor || !new RegExp(`(^|\\D)${kronor}(\\D|$)`).test(section[index]!.text)) return drop();
      return push(name, null, read);
    }

    // No line of its own: kept only if its amount is printed, and it is not
    // a second copy of a line already listed or of one below the total.
    if (read === null || !(printed.has(read) || printed.has(-read))) return drop();
    if (section.some((line) => claimed.has(line.index) && similar(item.name, line.text))) return drop();
    if (allLines.slice(section.length).some((line) => similar(item.name, line.text))) return drop();
    return push(name, null, read);
  });

  function push(name: string, row: ReceiptLine | null, read: Ore | null): void {
    const ore = row ? row.amounts[row.amounts.length - 1]! : read;
    if (ore === null || ore === 0) {
      dropped.push(current);
      return;
    }
    if (ore !== read) changed += 1;
    grounded.push({ name, totalPrice: formatOre(ore), lineIndex: row ? row.index : null, nameLine: currentLine });
  }

  return { items: grounded, changed, dropped };
}

const ADDRESS_NAME = /(gatan|vägen|vagen|väg\b|gata\b|\bplan\b|torget|allé|backe)\s*\d|\d{3}\s?\d{2}\s+\p{L}/iu;

/** A discount or saving, by its name: `Rabatt`, `Kampanj 25%`, `Mixrabatt`. */
const DISCOUNT_NAME = /^\W*(rabatt|kampanj|kampan\b|mixrabatt|medlemserbjudande|bonuscheck)/i;

/**
 * Lines the model passed over, put back when the total says they are missing.
 *
 * The model skips a row now and then - a discount (`ESS 2 -300,00 kr`), one
 * of a run of similar products. When the listed lines fall short of the total
 * by exactly the amount of one or two rows nobody claimed, those rows are the
 * missing ones. A name printed under its bare price (`10.00` over
 * `Doftblock sakura`) is the row's name, and a row whose amount OCR printed
 * with a stray separator (`POLLY ROD 200G 25/00`) is taken when it is the
 * missing amount exactly.
 *
 * The other way round, a discount the prices already include (Pressbyrån's
 * `FRUKOST 2F49: -16,00` under two lines that sum to 49), or a saving printed
 * again after the total (`Kampanj 25% 158.75`), is taken out when that alone
 * makes the lines add up.
 *
 * An exact match in öre is required, so a row is not added or taken out on a
 * near miss.
 */
export function fillFromTotal(items: GroundedItem[], section: readonly ReceiptLine[], total: Ore | null): GroundedItem[] {
  if (total === null || items.length === 0) return items;
  const sum = items.reduce((acc, item) => acc + (parseOre(item.totalPrice) ?? 0), 0);
  const gap = total - sum;
  if (gap === 0) return items;
  const claimed = new Set(items.map((item) => item.lineIndex).filter((index): index is number => index !== null));
  const first = Math.min(...[...claimed]);
  const candidates: GroundedItem[] = [];
  for (const line of section) {
    if (claimed.has(line.index) || line.index < first || line.amounts.length === 0 || isStop(line) || DETAIL.test(line.text)) continue;
    const above = section[line.index - 1];
    const below = section[line.index + 1];
    const bare = isPriceRow(line);
    const nameLine =
      bare && above && above.amounts.length === 0 && !claimed.has(above.index)
        ? above
        : bare && below && below.amounts.length === 0 && !claimed.has(below.index) && !isStop(below) && !DETAIL.test(below.text)
          ? below
          : line;
    const name = cleanItemName(nameLine.text);
    if (!name || !/\p{L}{2,}/u.test(name) || ADDRESS_NAME.test(name)) continue;
    candidates.push({ name, totalPrice: formatOre(line.amounts[line.amounts.length - 1]!), lineIndex: line.index, nameLine: nameLine.index });
  }
  const ore = (item: GroundedItem) => parseOre(item.totalPrice) ?? 0;
  const byLine = (added: GroundedItem[]) => [...items, ...added].sort((a, b) => (a.lineIndex ?? Infinity) - (b.lineIndex ?? Infinity));
  const single = candidates.find((candidate) => ore(candidate) === gap);
  if (single) return byLine([single]);
  const misread = gap > 0 ? misreadRow(section, claimed, first, gap) : null;
  if (misread) return byLine([misread]);
  for (let i = 0; i < candidates.length; i += 1) {
    for (let j = i + 1; j < candidates.length; j += 1) {
      if (ore(candidates[i]!) + ore(candidates[j]!) === gap) return byLine([candidates[i]!, candidates[j]!]);
    }
  }
  const spare = items.findIndex((item) => (ore(item) < 0 || DISCOUNT_NAME.test(item.name)) && ore(item) === -gap);
  if (spare >= 0) return items.filter((_, position) => position !== spare);
  return items;
}

/**
 * The product rows by rule alone, with no reader to point at them: a draft to
 * show while a model reads the receipt, or the lines when none ever does.
 *
 * Every row above the total that prints an amount and has a name is taken -
 * its own name, or for a bare price row (`1 * 199.00 199.00`) the name printed
 * just above or below it - the way `fillFromTotal` finds rows a model missed.
 * Then the space-grouped readings settle against the total, as for a model's
 * items. Whether the result adds up is `checkReading`'s to say.
 */
export function draftItems(lines: readonly ReceiptLine[], total: PaidTotal | null): GroundedItem[] {
  const section = purchaseSection(lines, total);
  const end = total ? total.lineIndex : section.length;
  const claimed = new Set<number>();
  const items: GroundedItem[] = [];
  for (const line of section) {
    if (line.index >= end) break;
    if (claimed.has(line.index) || line.amounts.length === 0 || isStop(line) || DETAIL.test(line.text) || isColumnHeader(line)) continue;
    const above = section[line.index - 1];
    const below = section[line.index + 1];
    const bare = isPriceRow(line);
    const free = (other: ReceiptLine | undefined): other is ReceiptLine =>
      Boolean(other) && other!.index < end && other!.amounts.length === 0 && !claimed.has(other!.index)
      && !isStop(other!) && !DETAIL.test(other!.text) && !isColumnHeader(other!);
    const nameLine = bare && free(above) ? above : bare && free(below) ? below : line;
    const name = cleanItemName(nameLine.text);
    if (!name || isFragment(name) || ADDRESS_NAME.test(name)) continue;
    claimed.add(line.index);
    claimed.add(nameLine.index);
    items.push({ name, totalPrice: formatOre(line.amounts[line.amounts.length - 1]!), lineIndex: line.index, nameLine: nameLine.index });
  }
  return settleSpacedAmounts(items, section, total?.ore ?? null);
}

/** An unclaimed row ending in `gap` printed with a stray separator, `25/00` for `25,00`. */
function misreadRow(section: readonly ReceiptLine[], claimed: ReadonlySet<number>, first: number, gap: Ore): GroundedItem | null {
  const printed = new RegExp(`(?<![\\d.,])${Math.trunc(gap / 100)}[^\\d\\s]${String(gap % 100).padStart(2, '0')}\\s*(kr)?\\s*$`, 'i');
  for (const line of section) {
    if (claimed.has(line.index) || line.index < first || line.amounts.length > 0 || isStop(line) || DETAIL.test(line.text)) continue;
    if (!printed.test(line.text)) continue;
    const name = cleanItemName(line.text.replace(printed, ''));
    if (name && /\p{L}{2,}/u.test(name)) return { name, totalPrice: formatOre(gap), lineIndex: line.index, nameLine: line.index };
  }
  return null;
}

/** A row's quantity times its unit price, `2st*13,90` → 2780, where it prints one. */
function multiplied(line: ReceiptLine | undefined): Ore[] {
  const match = line ? /(?<![\d.,])(\d{1,3})\s*(?:st|pcs)?\s*[*×x]\s*(\d+)[,.](\d{2})(?!\d)/i.exec(line.text) : null;
  return match ? [Number(match[1]) * (Number(match[2]) * 100 + Number(match[3]))] : [];
}

/**
 * Where the lines do not add up to the total by exactly one space-grouped
 * amount - `392400 1 299,00 kr` read as 299 rather than 1 299 - the line is
 * given the reading that makes them add up. So is a row whose line total OCR
 * misread beside a quantity and unit price it read right: `AVOKADO
 * 2st*13,90 21,80` is 27,80 when that is what the total needs.
 */
export function settleSpacedAmounts(items: GroundedItem[], section: readonly ReceiptLine[], total: Ore | null): GroundedItem[] {
  if (total === null) return items;
  const sum = items.reduce((acc, item) => acc + (parseOre(item.totalPrice) ?? 0), 0);
  if (sum === total) return items;
  for (const [position, item] of items.entries()) {
    if (item.lineIndex === null) continue;
    const current = parseOre(item.totalPrice) ?? 0;
    const line = section[item.lineIndex];
    const alternative = [...(line?.spaced ?? []), ...multiplied(line)].find((ore) => sum - current + ore === total);
    if (alternative !== undefined) {
      const next = [...items];
      next[position] = { ...item, totalPrice: formatOre(alternative) };
      return next;
    }
  }
  return items;
}

/**
 * The store's name, checked against the shop's own web address - printed at
 * the foot of most receipts, and read reliably where the logo at the top is
 * not (`CUMBY`, `PODER`, `HATVISI` for BAUHAUS and POWER). The address wins
 * when the model's name is missing, is a street address, or shares nothing
 * with it.
 */
export function merchantFallback(name: string | null | undefined, lines: readonly ReceiptLine[]): string | null {
  const read = name?.trim() ?? '';
  let site: string | null = null;
  for (const line of lines) {
    const match = /\bwww\.\s?([\p{L}\d-]{2,})\.\s?(se|com|nu|net)\b/iu.exec(line.text);
    if (match) {
      site = match[1]!;
      break;
    }
  }
  if (!site) return read || null;
  if (read && !ADDRESS_NAME.test(read)) {
    const a = fold(read);
    const b = fold(site);
    // Either side may be the misread one: `BAUHAUS` beside `www.bauhaijs.se`.
    if (a.includes(b) || b.includes(a) || similar(site, read) || similar(read, site)) return read;
  }
  const mixedCase = site !== site.toUpperCase() && site !== site.toLowerCase();
  return mixedCase ? site : site.charAt(0).toUpperCase() + site.slice(1).toLowerCase();
}

